/**
 * Chat message + source citation — wire shapes shared between WS events,
 * REST history endpoints, and the chat renderer.
 *
 * Kept intentionally narrow: the v1 ``Message`` type is a union of 30+
 * payload subtypes (``ResultPayload`` | ``TextPayload`` | …) that belongs
 * to ``apps/client/features/chat`` (it's not reused outside chat). Here we
 * only model the envelope every consumer needs.
 */

import { z } from "zod";
import { IsoTimestampSchema, UuidSchema } from "./common.js";
import { SourceTypeSchema } from "./source.js";

export const MessageRoleSchema = z.enum(["user", "assistant", "system"]);
export type MessageRole = z.infer<typeof MessageRoleSchema>;

/**
 * Single citation attached to an assistant message. Mirrors v1
 * ``ANDOSource`` minus the per-vertical extensions (email/web/chat fields
 * stay in their slices).
 *
 * ``source_type`` is required by the polymorphic auto-open hook
 * (``useSourceAutoOpen``) — when ``answer`` events land, it routes the
 * top-ranked citation to the matching drawer kind (document → PDF viewer,
 * email → email pane, web → external preview, …). The BE always emits it
 * on the wire (v1 ``ANDOSource.source_type``); we keep it optional here
 * for resilience against legacy fixtures lacking the field, but callers
 * SHOULD treat the absence as ``"document"`` (default bucket).
 */
export const CitationSchema = z.object({
  id: z.string(),
  doc_id: z.string().optional(),
  source_file: z.string().optional(),
  page: z.number().int().optional(),
  relevance_score: z.number().optional(),
  url: z.url().optional(),
  title: z.string().optional(),
  snippet: z.string().optional(),
  source_type: SourceTypeSchema.optional(),
});
export type Citation = z.infer<typeof CitationSchema>;

export const MessageSchema = z.object({
  id: UuidSchema,
  conversation_id: UuidSchema,
  user_id: UuidSchema,
  query_id: z.string().optional(),
  role: MessageRoleSchema,
  content: z.string(),
  citations: z.array(CitationSchema).optional(),
  timestamp: IsoTimestampSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type Message = z.infer<typeof MessageSchema>;
